<template>
  <el-container class="chat-page">
    <!-- 连接状态提示 -->
    <div v-if="!wsConnected" class="ws-disconnected-tip">
      <i class="el-icon-warning"></i>
      <span>连接已断开，正在尝试重连...</span>
    </div>
    <!-- 会话侧边栏 -->
    <el-aside  width="420px" class="combined-sidebar">
      <!-- 分组列表侧边栏 -->
      <div class="group-list-box">
        <div class="group-list-header">
          <span>分组列表</span>
          <el-button icon="el-icon-plus" size="mini" @click="showAddGroupDialog = true"></el-button>
        </div>
        <!-- 分组标签列表 -->
        <el-scrollbar class="group-scrollable-section">
          <el-menu
            :default-active="selectedGroup"
            class="group-menu"
            @select="handleGroupSelect"
          >
            <el-menu-item
              v-for="group in chatLabels" 
              :key="group.id"
              :index="String(group.id)"
              class="group-menu-item-with-delete"
            >
              <template slot="title">
                <i class="el-icon-chat-dot-round"></i>
                <span>{{ group.label }}</span> <!-- 分组名称 -->
                <span v-if="groupChatCounts[group.id] > 0">  <!-- 分组会话数量 -->
                  ({{ groupChatCounts[group.id] }})
                </span>
                <!-- 未读消息计数 -->
                <el-badge
                  v-if="groupUnreadCounts[group.id] > 0" 
                  :value="groupUnreadCounts[group.id]" 
                  :max="99"
                  class="group-unread-badge"
                ></el-badge>
                <!-- 删除按钮 -->
                <el-button
                  v-if="!group.system"
                  class="delete-group-btn"
                  icon="el-icon-close"
                  circle
                  size="mini"
                  @click.stop="deleteGroup(group)"
                ></el-button>
              </template>
            </el-menu-item>
          </el-menu>
        </el-scrollbar>
      </div>
      <!-- 聊天列表 -->
      <div class="chat-list-box">
        <!-- <resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="chat-aside-width"> -->
          <div class="header">
            <el-input class="search-text" size="small" placeholder="搜索" v-model="searchText">
              <i class="el-icon-search el-input__icon" slot="prefix"> </i>
            </el-input>
            <!-- 企微对企微主动发起新会话（内部同事/企微好友） -->
            <el-button
              class="start-chat-btn"
              size="small"
              icon="el-icon-plus"
              circle
              title="发起会话"
              @click="showStartChat = true"
            ></el-button>
          </div>
          <div class="chat-loading" v-if="uiLoading" v-loading="true" element-loading-text="消息接收中..."
            element-loading-spinner="el-icon-loading" element-loading-background="#F9F9F9" element-loading-size="24">
          </div>
          <!-- 会话列表 -->
          <div class="chat-list-container" v-else>
            <!-- 单一滚动容器：置顶/普通分区统一虚拟滚动（对齐主流 IM，消除双滚动容器） -->
            <div ref="sessionScrollWrap" class="scrollable-section-virtual" @scroll="onSessionScroll">
              <template v-if="virtualItems.length > 0">
                <div v-if="topSpacerHeight > 0" :style="{ height: topSpacerHeight + 'px' }"></div>
                <template v-for="item in visibleVirtualItems">
                  <!-- 分区分隔标题行（置顶聊天/全部会话），搜索态下不显示；key 加类型前缀保证 v-if/v-else 分支 key 唯一 -->
                  <div v-if="item.kind === 'header'" :key="'h-' + item.key" class="section-header">{{ item.label }}</div>
                  <div v-else :key="'c-' + item.key" :data-conversation-id="item.chat.conversationID">
                    <chat-item
                      :chat="item.chat"
                      :index="getOriginalIndex(item.chat)"
                      @click.native="onActiveItem(getOriginalIndex(item.chat))"
                      @dnd="onDnd(item.chat)"
                      :isPinned="item.chat.isPinned === true"
                      :active="item.chat === chatStore.activeChat">
                    </chat-item>
                  </div>
                </template>
                <div v-if="bottomSpacerHeight > 0" :style="{ height: bottomSpacerHeight + 'px' }"></div>
              </template>
              <div class="list-empty-tip" v-else>
                <i class="el-icon-chat-dot-round"></i>
                <span>{{ debouncedSearchText ? '未找到相关会话' : '暂无会话' }}</span>
              </div>
            </div>
        </div>
        <!-- </resizable-aside> -->
      </div>
    </el-aside>
    <!-- 聊天框：未选中会话时渲染同尺寸占位骨架，避免中间列整片空白 -->
    <el-container>
      <chat-box v-if="chatStore.activeChat" :chat="chatStore.activeChat"></chat-box>
      <empty-chat-box v-else></empty-chat-box>
    </el-container>
    <!-- 快捷回侧边栏  -->
    <el-aside width="420px">
      <quick-reply></quick-reply>
    </el-aside>

    <!-- 添加分组标签对话框 -->
    <el-dialog
      title="添加分组标签"
      :visible.sync="showAddGroupDialog"
      width="30%"
      :before-close="handleCloseDialog"
    >
      <el-input
        v-model="newGroupName"
        placeholder="请输入分组标签名称"
        maxlength="6"
        show-word-limit
      ></el-input>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showAddGroupDialog = false">取 消</el-button>
        <el-button type="primary" @click="addNewGroup">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 发起会话（企微对企微：内部同事/企微好友） -->
    <start-chat-dialog :visible.sync="showStartChat" />
  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import EmptyChatBox from "../components/chat/EmptyChatBox.vue";
// import ResizableAside from "../components/common/ResizableAside.vue";
import QuickReply from "../components/chat/QuickReply.vue";
import StartChatDialog from "../components/chat/StartChatDialog.vue";
import { addConversationLabel, deleteConversationLabel, dndConversation } from "../api/conversation.js"
import { SYSTEM_GROUPS } from '../utils/conversation.js';

// 分区分隔标题行（置顶聊天/全部会话）固定高度，需与样式 .section-header 的 height 保持一致
const HEADER_ITEM_HEIGHT = 32;

export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    EmptyChatBox,
    // ResizableAside
    QuickReply, // 注册组件
    StartChatDialog
  },
  data() {
    return {
      searchText: "",
      showStartChat: false, // 发起会话对话框
      messageContent: "",
      group: {},
      groupMembers: [],
      showAddGroupDialog: false,
      newGroupName: "",
      selectedGroup: "0",
      wsConnected: true,
      isDestroyed: false,
      // 虚拟滚动 — 动态高度（测量 + 前缀和）
      visStart: 0,
      visEnd: 0,
      defaultItemHeight: 56,
      bufferSize: 8,
      sessionHeights: {},
      heightPrefixSum: null,
      resizeObserver: null,
      scrollTicking: false,
      searchDebounceTimer: null,
      debouncedSearchText: '',
    }
  },
  methods: {
    // 获取会话在原始列表中的索引（O(1) 通过 chatIndexMap）
    getOriginalIndex(chat) {
      if (!chat || !chat.conversationID) return -1;
      const idx = this.chatStore.chatIndexMap[chat.conversationID];
      return idx !== undefined ? idx : this.chatStore.chats.findIndex(
        c => c.conversationID === chat.conversationID);
    },
    // 虚拟滚动：根据滚动位置计算可见范围
    onSessionScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.recalcVisibleRange();
        this.scrollTicking = false;
      });
    },
    recalcVisibleRange() {
      const wrap = this.$refs.sessionScrollWrap;
      if (!wrap) return;
      const scrollTop = wrap.scrollTop;
      const containerHeight = wrap.clientHeight;
      const list = this.virtualItems;
      const total = list.length;
      if (total === 0 || containerHeight === 0) {
        this.visStart = 0;
        this.visEnd = 0;
        return;
      }
      const prefix = this.heightPrefixSum;
      if (prefix && prefix.length === total + 1) {
        let lo = 0, hi = total;
        while (lo < hi) {
          const mid = (lo + hi + 1) >> 1;
          if (prefix[mid] <= scrollTop) lo = mid; else hi = mid - 1;
        }
        this.visStart = lo;
        const target = scrollTop + containerHeight;
        hi = total;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (prefix[mid + 1] < target) lo = mid + 1; else hi = mid;
        }
        this.visEnd = Math.min(total, lo + 1);
      } else {
        const start = Math.floor(scrollTop / this.defaultItemHeight);
        const visibleCount = Math.ceil(containerHeight / this.defaultItemHeight);
        this.visStart = Math.max(0, start);
        this.visEnd = Math.min(total, start + visibleCount + 1);
      }
      // 等 Vue 渲染完新一批 ChatItem 后测量实际高度
      this.$nextTick(() => this._measureVisible());
    },
    _measureVisible() {
      if (this.isDestroyed) return;
      const wrap = this.$refs.sessionScrollWrap;
      if (!wrap) return;
      const items = wrap.querySelectorAll('[data-conversation-id]');
      if (!items.length) return;
      let changed = false;
      for (let i = 0; i < items.length; i++) {
        const cid = items[i].dataset.conversationId;
        if (!cid) continue;
        const h = items[i].offsetHeight;
        if (h > 0 && this.sessionHeights[cid] !== h) {
          this.sessionHeights[cid] = h;
          changed = true;
        }
      }
      if (changed) this._buildPrefixSum();
    },
    _buildPrefixSum() {
      const list = this.virtualItems;
      if (list.length === 0) {
        this.heightPrefixSum = null;
        return;
      }
      const sum = new Array(list.length + 1);
      sum[0] = 0;
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        // 会话项用实测高度（未测则用默认值），标题行用固定高度
        const h = item.kind === 'chat'
          ? (this.sessionHeights[item.chat.conversationID] || this.defaultItemHeight)
          : HEADER_ITEM_HEIGHT;
        sum[i + 1] = sum[i] + h;
      }
      this.heightPrefixSum = sum;
    },
    _ensurePrefixSum() {
      if (!this.heightPrefixSum || this.heightPrefixSum.length !== this.virtualItems.length + 1) {
        this._buildPrefixSum();
      }
    },
    _resetSessionHeights() {
      this.sessionHeights = {};
      this.heightPrefixSum = null;
    },
    onActiveItem(index) {
      this.chatStore.setActiveChat(index);
    },
    onDnd(chat) {
      // 屏蔽群消息（消息免打扰）当前仅群聊会话支持（单聊沿用行内字段体系未启用）
      if (chat.convType !== 'group') {
        return;
      }
      this.setGroupDnd(chat, !chat.isDnd)
    },

    // 处理会话分组选择
    handleGroupSelect(index) {
      if (this.selectedGroup === index) return;
      this.selectedGroup = index;
    },
    // 添加新分组标签
    addNewGroup() {
      let label = this.newGroupName.trim()
      if (!label){
        return
      }
      addConversationLabel({label: label}).then((data) => {
        this.chatStore.addChatLabel(data);
        this.$message.success('创建分组标签成功');
      }).finally(() => {
        this.showAddGroupDialog = false;
        this.newGroupName = "";
      });
    },
    // 删除分组标签
    deleteGroup(group) {
      this.$confirm(`确定要删除分组"${group.label}"吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteConversationLabel({label: group.label}).then(() => {
          this.chatStore.deleteChatLabel(group);
          this.$message.success('删除成功');
        })
      }).catch(() => {
        // 取消删除
      });
    },
    // 关闭分组对话框
    handleCloseDialog(done) {
      this.newGroupName = "";
      done();
    },
    setGroupDnd(chat, isDnd) {
      let formData = {
        conversationID: chat.conversationID,
        isDnd: isDnd
      }
      dndConversation(formData).then(() => {
        this.chatStore.setDnd(chat, isDnd)
        this.$message.success(isDnd ? '已屏蔽该群消息' : '已恢复该群消息提醒');
      }).catch(err => {
        this.$message.error('操作失败: ' + err.message);
      })
    }
  },
  mounted() {
    // 监听WebSocket连接状态
    this.$eventBus.$on('ws-disconnected', () => {
      this.wsConnected = false;
    });
    this.$eventBus.$on('ws-connected', () => {
      this.wsConnected = true;
    });
  },
  watch: {
    // uiLoading 结束后 DOM 才渲染 sessionScrollWrap（silent 周期同步不触发，
    // 避免其结束时的净变化为 false→false 时仍重建高度缓存）
    uiLoading: {
      handler(val) {
        if (val) return;
        this.$nextTick(() => {
          if (!this.$refs.sessionScrollWrap) return;
          this._resetSessionHeights();
          this._buildPrefixSum();
          this.recalcVisibleRange();
          // 窗口 resize 时 clientHeight 变化，只重算不重测
          if (typeof ResizeObserver !== 'undefined' && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.recalcVisibleRange());
            this.resizeObserver.observe(this.$refs.sessionScrollWrap);
          }
        });
      },
      immediate: true,
    },
    // 切换分组 → 列表完全变化，重置测量
    selectedGroup() {
      this.$nextTick(() => {
        this._resetSessionHeights();
        this._buildPrefixSum();
        this.recalcVisibleRange();
      });
    },
    // 搜索文本变化 → 列表过滤变化，重置测量
    debouncedSearchText() {
      this.$nextTick(() => {
        this._resetSessionHeights();
        this._buildPrefixSum();
        this.recalcVisibleRange();
      });
    },
    // 列表重排序/分区结构变化 → 只重建前缀和，保留已测高度
    _virtualItemsKey: {
      handler() {
        this.$nextTick(() => {
          this._buildPrefixSum();
          this.recalcVisibleRange();
        });
      },
    },
    // 性能优化：搜索输入 150ms 防抖
    searchText: {
      handler(val) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = setTimeout(() => {
          this.debouncedSearchText = val;
        }, 150);
      },
    },
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.$eventBus.$off('ws-disconnected');
    this.$eventBus.$off('ws-connected');
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  computed: {
    // 统一虚拟列表条目：置顶标题行 + 置顶会话 + 全部会话标题行 + 普通会话
    // 搜索激活时置顶与普通命中合并为单一结果列表（无分区标题），与主流 IM 搜索交互一致
    virtualItems() {
      const pinned = this.filteredPinnedChats;
      const normal = this.filteredNormalChats;
      const items = [];
      if (this.debouncedSearchText) {
        // 搜索态：置顶会话同样参与搜索命中，避免遗漏置顶客户
        pinned.forEach(chat => items.push({ kind: 'chat', key: chat.conversationID, chat }));
      } else {
        if (pinned.length > 0) {
          items.push({ kind: 'header', key: 'hdr-pinned', label: '置顶聊天' });
          pinned.forEach(chat => items.push({ kind: 'chat', key: chat.conversationID, chat }));
        }
        if (normal.length > 0) {
          items.push({ kind: 'header', key: 'hdr-all', label: '全部会话' });
        }
      }
      normal.forEach(chat => items.push({ kind: 'chat', key: chat.conversationID, chat }));
      return items;
    },
    // 虚拟滚动：只渲染可见区域 + buffer 内的条目
    visibleVirtualItems() {
      const list = this.virtualItems;
      if (list.length === 0) return [];
      const buf = this.bufferSize;
      const start = Math.max(0, this.visStart - buf);
      const end = Math.min(list.length, this.visEnd + buf);
      return list.slice(start, end);
    },
    topSpacerHeight() {
      const list = this.virtualItems;
      if (list.length === 0) return 0;
      const buf = this.bufferSize;
      const idx = Math.max(0, this.visStart - buf);
      if (this.heightPrefixSum && this.heightPrefixSum.length === list.length + 1) {
        return this.heightPrefixSum[idx] || 0;
      }
      return idx * this.defaultItemHeight;
    },
    bottomSpacerHeight() {
      const list = this.virtualItems;
      if (list.length === 0) return 0;
      const buf = this.bufferSize;
      const end = Math.min(list.length, this.visEnd + buf);
      if (this.heightPrefixSum && this.heightPrefixSum.length === list.length + 1) {
        return (this.heightPrefixSum[list.length] || 0) - (this.heightPrefixSum[end] || 0);
      }
      return Math.max(0, list.length - end) * this.defaultItemHeight;
    },
    // 需要显示"消息接收中"占位/重建列表缓存的加载态：周期静默同步（silentLoading）
    // 虽处于数据面翻转窗口（chatStore.loading=true），但不打断已渲染的会话列表
    uiLoading() {
      return this.chatStore.loading && !this.chatStore.silentLoading;
    },
    // 分组标签：默认组（兜底，不可删除）+ 客服自建分组（已删除/已拉黑系统分组已移除）
    chatLabels() {
      return [
        { ...SYSTEM_GROUPS.DEFAULT, system: true },
        ...this.chatStore.chatLabels.map(l => ({ ...l, system: false }))
      ];
    },
    // 按分组聚合后的数据，由 Pinia getter 缓存（只在 chats 引用变化时重算）
    groupData() {
      const byGroup = this.chatStore.chatsByGroup;
      const selectedId = parseInt(this.selectedGroup);
      return byGroup[selectedId] || { pinned: [], normal: [], unreadCount: 0, chatCount: 0 };
    },
    pinnedChats() {
      return this.groupData.pinned;
    },
    normalChats() {
      return this.groupData.normal;
    },
    // 列表摘要 key — 重排序/分区结构变化时值变化，触发 watcher 重建前缀和。
    // 除首/中/尾三点采样外，纳入置顶/普通分区计数与搜索态：
    // 取消置顶等分区迁移可能不触碰任何采样点（总长度不变、三点不变），
    // 但分区计数必然变化，确保前缀和重建不漏检。
    _virtualItemsKey() {
      const list = this.virtualItems;
      if (list.length <= 1) return list.length;
      const n = Math.min(3, Math.floor(list.length / 2));
      const head = list[0] && list[0].key;
      const mid = list[n] && list[n].key;
      const tail = list[list.length - 1] && list[list.length - 1].key;
      return list.length + '|' + this.filteredPinnedChats.length + '|' + this.filteredNormalChats.length
        + (this.debouncedSearchText ? '|s' : '|n') + '|' + head + '|' + mid + '|' + tail;
    },
    // 性能优化：从 chatStore._groupSummary 读取增量缓存，O(1)
    groupUnreadCounts() {
      const counts = {};
      this.chatLabels.forEach(g => {
        const s = this.chatStore._groupSummary[g.id];
        counts[g.id] = s ? s.unreadCount : 0;
      });
      return counts;
    },
    // 性能优化：从 chatStore._groupSummary 读取增量缓存，O(1)
    groupChatCounts() {
      const counts = {};
      this.chatLabels.forEach(g => {
        const s = this.chatStore._groupSummary[g.id];
        counts[g.id] = s ? s.chatCount : 0;
      });
      return counts;
    },
    // 搜索过滤：支持搜索会话名称、备注、最新消息内容
    // 性能优化：使用防抖后的搜索词，避免每次按键都 O(n) 过滤
    filteredPinnedChats() {
      const chats = this.pinnedChats.filter(chat => chat);
      if (!this.debouncedSearchText) return chats;
      const s = this.debouncedSearchText.toLowerCase();
      return chats.filter(c =>
        (c.showName || '').toLowerCase().includes(s) ||
        (c.remark || '').toLowerCase().includes(s) ||
        (c.latestMsg || '').toLowerCase().includes(s)
      );
    },
    filteredNormalChats() {
      const chats = this.normalChats.filter(chat => chat);
      if (!this.debouncedSearchText) return chats;
      const s = this.debouncedSearchText.toLowerCase();
      return chats.filter(c =>
        (c.showName || '').toLowerCase().includes(s) ||
        (c.remark || '').toLowerCase().includes(s) ||
        (c.latestMsg || '').toLowerCase().includes(s)
      );
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-page {
  // 连接断开提示
  .ws-disconnected-tip {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #fff3e0;
    color: #e65100;
    padding: 10px 20px;
    text-align: center;
    font-size: 14px;
    z-index: 9999;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    
    i {
      margin-right: 8px;
      font-size: 16px;
    }
  }
  
  // 合并的侧边栏样式
  .combined-sidebar {
    display: flex;
    height: 100%;
    background: #f5f7fa;
    border-right: 1px solid #e6e6e6;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
    // 新增分组列表样式
    .group-list-box {
      width: 160px;
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #f5f7fa;
      border-right: 1px solid #e6e6e6;

      .group-list-header {
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 15px;
        font-weight: bold;
        color: #333;
        border-bottom: 1px solid #e6e6e6;
        background: #fff;
        
        .el-button {
          padding: 5px;
          min-width: auto;
        }
      }

      .group-scrollable-section {
        flex: 1;
        overflow-y: auto;

        .group-menu {
          border: none;
          background: transparent;
          padding: 6px 0;

          .group-menu-item-with-delete {
            display: flex;
            align-items: center;
            height: 36px;
            line-height: 36px;
            padding: 0 1px;
            margin: 1px 1px;
            border-radius: 1px;
            position: relative;
            
            .el-tooltip {
              display: flex;
              align-items: center;
              width: 100%;
            }

            .delete-group-btn {
              position: absolute;
              right: 6px;
              top: 50%;
              transform: translateY(-50%);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 14px;
              height: 14px;
              min-width: auto;
              padding: 0;
              border: none;
              background: transparent;
              opacity: 0;
              transition: opacity 0.2s ease;
              z-index: 2;

              .el-icon-close {
                font-size: 10px;
                color: #909399;
                line-height: 1;
              }

              &:hover {
                opacity: 1 !important;
                background-color: #f56c6c;

                .el-icon-close {
                  color: white;
                }
              }
            }
            
            i.el-icon-chat-dot-round {
              margin-right: 8px;
              font-size: 14px;
              color: #606266;
              flex-shrink: 0;
            }
            
            .group-label-text {
              flex: 1;
              font-size: 13px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              padding-right: 18px; // 为删除按钮预留空间
            }
            
            .group-unread-badge {
              position: absolute;
              right: 30px; // 调整位置，避免与删除按钮重叠
              top: 10px;
              
              .el-badge__content {
                height: 16px;
                line-height: 16px;
                padding: 0 4px;
                font-size: 10px;
                border-radius: 8px;
              }
            }

            &:hover {
              background-color: #ecf5ff;

              .delete-group-btn {
                opacity: 0.6;
              }
            }
            
            &.is-active {
              color: #409EFF;
              background-color: #ecf5ff;
              
              i.el-icon-chat-dot-round {
                color: #409EFF;
              }

              .delete-group-btn .el-icon-close {
                color: #69c0ff;
              }
            }
          }
        }
      }

      .group-list-footer {
        padding: 10px;
        border-top: 1px solid #e6e6e6;
        background: #fff;
      }
    }
    // 会话列表
    .chat-list-box {
      width: 260px;
      display: flex;
      flex-direction: column;
      height: 100%; // 确保高度充满父容器
      background: var(--im-background);

      .header {
        height: 50px;
        display: flex;
        align-items: center;
        padding: 0 8px;
        flex-shrink: 0; // 防止搜索框区域被压缩

        .search-text {
          flex: 1;
          min-width: 0;
        }

        .start-chat-btn {
          margin-left: 8px;
          flex-shrink: 0;
        }
      }

      .chat-loading {
        height: 50px;
        background-color: #eee;

        .el-icon-loading {
          font-size: 24px;
          color: var(--im-text-color-light);
        }

        .el-loading-text {
          color: var(--im-text-color-light);
        }
      }

      .chat-list-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden; // 隐藏外层容器滚动
    
        /* 单一滚动容器：置顶/普通分区统一虚拟滚动 */
        .scrollable-section-virtual {
          flex-grow: 1;
          overflow-y: auto;
          overflow-x: hidden;
          will-change: scroll-position;

          /* 分区分隔标题行（置顶聊天/全部会话），高度需与 HEADER_ITEM_HEIGHT 常量一致 */
          .section-header {
            box-sizing: border-box; // height 含 border-bottom，保证实际渲染高度 = HEADER_ITEM_HEIGHT
            height: 32px;
            line-height: 31px; // 内容区 31px，抵消 border-bottom 保持文字垂直居中
            padding: 0 16px;
            font-size: 12px;
            color: #999;
            background: #f9f9f9;
            border-bottom: 1px solid #f0f0f0;
            user-select: none;
          }

          /* 空态提示 */
          .list-empty-tip {
            padding: 60px 0;
            text-align: center;
            color: var(--im-text-color-lighter);
            font-size: 13px;

            i {
              display: block;
              font-size: 32px;
              margin-bottom: 8px;
            }
          }
        }
      }
    }
  }
}
</style>
